// ---------------------------------------------------------------------------
// Messaging Provider Configurations — encrypted credentials per channel
// ---------------------------------------------------------------------------
// Sprint 1 Stream C: Stores provider credentials for SMS, WhatsApp, Telegram, etc.
// ALL credential fields are encrypted at rest using per-tenant KEK via @ease/crypto.
// Fields prefixed with enc_ store encrypted values (enc:v1:... format).
//
// Security invariants:
// - Raw credentials NEVER stored in plaintext
// - Raw credentials NEVER logged
// - Decryption requires tenant KEK (derived from PMK + encrypted_kek in control plane)
// - GDPR erasure: DELETE WHERE tenant_id = X (or DROP SCHEMA CASCADE for shared DBs)
// ---------------------------------------------------------------------------

import { pgTable, uuid, text, jsonb, boolean, integer, timestamp, index, uniqueIndex } from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

export const MESSAGING_CHANNELS = [
  "sms", "whatsapp", "email", "telegram", "messenger", "viber", "signal",
] as const;
export type MessagingChannel = (typeof MESSAGING_CHANNELS)[number];

export const MESSAGING_PROVIDERS = [
  "telnyx", "sinch", "ses", "360dialog", "meta", "telegram", "infobip", "signal_business",
] as const;
export type MessagingProvider = (typeof MESSAGING_PROVIDERS)[number];

export const messagingProviderConfigs = pgTable(
  "messaging_provider_configs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    channel: text("channel").notNull(), // sms, whatsapp, telegram, etc.
    provider: text("provider").notNull(), // telnyx, sinch, 360dialog, meta, telegram, etc.
    role: text("role").notNull().default("primary"), // primary, fallback

    // Encrypted credential fields (enc:v1:... format via @ease/crypto)
    encApiKey: text("enc_api_key"), // Encrypted API key
    encApiSecret: text("enc_api_secret"), // Encrypted API secret (Sinch)
    encAccessToken: text("enc_access_token"), // Encrypted OAuth access token (Meta)
    encBotToken: text("enc_bot_token"), // Encrypted bot token (Telegram)

    // Non-sensitive config
    phoneNumber: text("phone_number"), // E.164 format
    phoneNumberId: text("phone_number_id"), // WhatsApp phone number ID
    businessManagerId: text("business_manager_id"), // Meta Business Manager ID
    webhookUrl: text("webhook_url"), // Auto-configured webhook URL
    webhookSecretToken: text("webhook_secret_token"), // Telegram webhook secret

    // Status
    isEnabled: boolean("is_enabled").notNull().default(true),
    isValidated: boolean("is_validated").notNull().default(false),
    lastValidatedAt: timestamp("last_validated_at", tz),
    lastErrorAt: timestamp("last_error_at", tz),
    lastErrorMessage: text("last_error_message"),
    consecutiveFailures: integer("consecutive_failures").notNull().default(0),

    // Metadata
    providerAccountInfo: jsonb("provider_account_info").$type<Record<string, unknown>>(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_msg_provider_configs_tenant").on(table.tenantId),
    uniqueIndex("idx_msg_provider_configs_unique").on(table.tenantId, table.channel, table.role),
    index("idx_msg_provider_configs_channel").on(table.tenantId, table.channel),
  ],
);

// Routing rules per channel (priority + failover config)
export const messagingRoutingRules = pgTable(
  "messaging_routing_rules",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    channel: text("channel").notNull(),
    providerPriority: jsonb("provider_priority").$type<Array<{ providerId: string; priority: number }>>().notNull().default([]),
    failoverThreshold: integer("failover_threshold").notNull().default(3),
    manualOverride: boolean("manual_override").notNull().default(false),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_msg_routing_rules_unique").on(table.tenantId, table.channel),
  ],
);
